<template>
	<v-card variant="outlined">
		<v-card-text class="d-flex flex-row align-center">
			<div class="w-75 mx-2">
				<WaveformWidget v-if="asset"
					@update:marker="handleUpdateMarkerValue"
					@update:region="handleUpdateRegion"/>
				<div v-else
					class="dropzone"
					:class="{dropzoneActive}"
					v-bind="dropzoneProps">
					<input v-bind="dropzoneInputProps"/>
					<v-icon :icon="fileDropped ? 'mdi-upload' :'mdi-content-save'" size="300" color="primary"/>
				</div>
			</div>

			<div class="w-25 mx-2">
				<v-form>
					<div class="d-flex flex-row align-center"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap my-3 border-b-sm py-2' : 'flex-nowrap'">
						<v-col class="fade-in-tab"
							:class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<v-icon icon="mdi-circle" color="red" size="8"/>
							<label class="text-caption"> Fade In </label>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field :model-value="fadeInModelValue"
								placeholder="Fade In"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('fadeIn', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								color="secondary"
								variant="outlined"
								:disabled="!editAssetFeatureEnabled"
								@click="setMarkerToTime('fadeIn')"
							>
								Go to
							</v-btn>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled"
								@click="setTimeToMarker('fadeIn')">
								Set
							</v-btn>
						</v-col>
					</div>

					<div class="d-flex flex-row align-center"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap border-b-sm py-2 my-3' : 'flex-nowrap'">
						<v-col class="fade-in-tab" :class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<label class="text-caption">Length </label>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field :model-value="fadeInLengthModelValue"
								placeholder="Length"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('fadeInLength', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 offset-3 py-0' : 'v-col-2 offset-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled"
							>
								Set
							</v-btn>
						</v-col>
					</div>

					<div class="d-flex flex-row my-lg-3 align-center"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap border-b-sm py-2 my-3' : 'flex-nowrap'">
						<v-col class="fade-out-tab" :class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<v-icon icon="mdi-circle" color="red" size="8"/>
							<label class="text-caption"> Fade Out </label>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field :model-value="fadeOutModelValue"
								placeholder="Fade Out"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('fadeOut', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								color="secondary"
								variant="outlined"
								:disabled="!editAssetFeatureEnabled"
								@click="setMarkerToTime('fadeOut')"
							>
								Go to
							</v-btn>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled"
								@click="setTimeToMarker('fadeOut')">
								Set
							</v-btn>
						</v-col>
					</div>

					<div class="d-flex flex-row align-center border-b-sm"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap py-2 my-3' : 'flex-nowrap'">
						<v-col class="fade-out-tab" :class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<label class="text-caption"> Length </label>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field :model-value="fadeOutLengthModelValue"
								placeholder="Length"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('fadeOutLength', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 offset-3 py-0' : 'v-col-2 offset-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled">
								Set
							</v-btn>
						</v-col>
					</div>

					<div class="d-flex flex-row align-center"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap py-2 my-3' : 'flex-nowrap'">
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<v-icon icon="mdi-circle" color="rgb(255, 189, 0)" size="8"/>
							<label class="text-caption mr-3"> Intro </label>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field :model-value="introModelValue"
								placeholder="Intro"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('intro', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								color="secondary"
								variant="outlined"
								:disabled="!editAssetFeatureEnabled"
								@click="setMarkerToTime('intro')"
							>
								Go to
							</v-btn>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled"
								@click="setTimeToMarker('intro')">
								Set
							</v-btn>
						</v-col>
					</div>

					<div v-if="formData.intro2" class="d-flex flex-row align-center"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap py-2 my-3' : 'flex-nowrap'">
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<v-icon icon="mdi-circle" color="rgb(255, 189, 0)" size="8"/>
							<label class="text-caption mr-3"> Intro 2 </label>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field :model-value="intro2ModelValue"
								placeholder="Intro 2"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('intro2', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								color="secondary"
								variant="outlined"
								:disabled="!editAssetFeatureEnabled"
								@click="setMarkerToTime('intro2')"
							>
								Go to
							</v-btn>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled"
								@click="setTimeToMarker('intro2')">
								Set
							</v-btn>
						</v-col>
					</div>

					<div v-if="formData.intro3" class="d-flex flex-row align-center"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap py-2 my-3' : 'flex-nowrap'">
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<v-icon icon="mdi-circle" color="rgb(255, 189, 0)" size="8"/>
							<label class="text-caption mr-3"> Intro 3</label>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field :model-value="intro3ModelValue"
								placeholder="Intro"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('intro3', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								color="secondary"
								variant="outlined"
								:disabled="!editAssetFeatureEnabled"
								@click="setMarkerToTime('intro3')"
							>
								Go to
							</v-btn>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled"
								@click="setTimeToMarker('intro3')">
								Set
							</v-btn>
						</v-col>
					</div>

					<div v-if="!formData.intro3" class="d-flex flex-row align-center border-b-sm">
						<v-btn class="mb-3"
							color="secondary"
							prepend-icon="mdi-plus-circle-outline"
							:disabled="!editAssetFeatureEnabled"
							@click="addIntro">
							Add Intro
						</v-btn>
					</div>

					<div class="d-flex flex-row align-center"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap border-b-sm py-2 my-3' : 'flex-nowrap'">
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<v-icon icon="mdi-circle" color="purple" size="8"/>
							<label class="text-caption mr-3"> Early Segue </label>
							<v-btn :class="$vuetify.display.lgAndDown ? '' :'d-none'"
								:icon="assetLocked ? 'mdi-lock' : 'mdi-lock-open-variant'"
								variant="flat"
								rounded
								:disabled="!editAssetFeatureEnabled"
								@click="toggleAssetLocked"/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field :model-value="earlySegueModelValue"
								placeholder="Early Segue"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('earlySegue', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								color="secondary"
								variant="outlined"
								:disabled="!editAssetFeatureEnabled"
								@click="setMarkerToTime('earlySegue')"
							>
								Go to
							</v-btn>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled"
								@click="setTimeToMarker('earlySegue')">
								Set
							</v-btn>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'd-none' : 'v-col-1'">
							<v-btn
								:icon="assetLocked ? 'mdi-lock' : 'mdi-lock-open-variant'"
								variant="flat"
								rounded
								:disabled="!editAssetFeatureEnabled"
								@click="toggleAssetLocked"/>
						</v-col>
					</div>

					<div class="d-flex flex-row align-center"
						:class="$vuetify.display.xlAndDown ? 'flex-wrap py-2 my-3' : 'flex-nowrap'">
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-12 pa-0' : 'v-col-2'">
							<v-icon icon="mdi-circle" color="green" size="8"/>
							<label class="text-caption mr-3">Segue </label>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-6 py-0' : 'v-col-5'">
							<v-text-field
								:model-value="segueModelValue"
								type="text"
								placeholder="Segue"
								variant="outlined"
								density="compact"
								hide-details
								clearable
								:disabled="!editAssetFeatureEnabled"
								@update:model-value="value => debouncedUpdateFormData('segue', value)"
							/>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								color="secondary"
								variant="outlined"
								:disabled="!editAssetFeatureEnabled"
								@click="setMarkerToTime('segue')"
							>
								Go to
							</v-btn>
						</v-col>
						<v-col :class="$vuetify.display.xlAndDown ? 'v-col-3 py-0' : 'v-col-2'">
							<v-btn
								:disabled="!editAssetFeatureEnabled"
								@click="setTimeToMarker('segue')">
								Set
							</v-btn>
						</v-col>
					</div>
				</v-form>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
/* Modules */
import WaveformWidget from "@/components/WaveformWidget.vue";

/* Enums */
import POITypes from "@/enums/POITypes";
import Modes from "@/enums/AssetManagementModeTypes";

/* Mixins */
import DurationFormatMixin from "@/mixins/DurationFormatMixin";
import AssetProperties from "@/mixins/AssetProperties";

/* Libraries */
import emitter from "@/emitter";
import {useDropzone} from "vue3-dropzone";
import _ from "lodash";

export default {
	name: "AssetManagementGlobalMarksTab",

	components: {WaveformWidget},

	mixins: [DurationFormatMixin, AssetProperties],

	data() {
		return {
			formData: {},
			payload: {},
			markerValue: null,
			dropzoneProps: null,
			dropzoneInputProps: null,
			dropzoneRest: {},
			dropzoneActive: false,
			dropzoneUploadLoading: false,
			fileDropped: false
		}
	},

	computed: {
		event() {
			return this.$store.getters.getAssetManagementEvent();
		},

		asset() {
			return this.$store.getters.getAssetManagementAsset();
		},

		mode() {
			return this.$store.getters.getAssetManagementMode();
		},

		assetLocked() {
			return this.formData?.POIMarkup?.POIMarkers.find(p => p.POIType === 104)?.Static;
		},

		fadeInModelValue() {
			return this.formData.fadeIn ? this.secondsToFormat(this.formData.fadeIn, 'mm:ss') : this.formData.fadeIn;
		},

		fadeInLengthModelValue() {
			return this.formData.fadeInLength ? this.secondsToFormat(this.formData.fadeInLength, 'mm:ss') :
				   this.formData.fadeInLength;
		},

		fadeOutModelValue() {
			return this.formData.fadeOut ? this.secondsToFormat(this.formData.fadeOut, 'mm:ss') : this.formData.fadeOut;
		},

		fadeOutLengthModelValue() {
			return this.formData.fadeOutLength ? this.secondsToFormat(this.formData.fadeOutLength, 'mm:ss') :
				   this.formData.fadeOutLength;
		},

		introModelValue() {
			return this.formData.intro ? this.secondsToFormat(this.formData.intro, 'mm:ss') : this.formData.intro;
		},

		intro2ModelValue() {
			return this.formData.intro2 ? this.secondsToFormat(this.formData.intro2, 'mm:ss') : this.formData.intro2;
		},

		intro3ModelValue() {
			return this.formData.intro3 ? this.secondsToFormat(this.formData.intro3, 'mm:ss') : this.formData.intro3;
		},

		segueModelValue() {
			return this.formData.segue ? this.secondsToFormat(this.formData.segue, 'mm:ss') : this.formData.segue;
		},

		earlySegueModelValue() {
			return this.formData.earlySegue ? this.secondsToFormat(this.formData.earlySegue, 'mm:ss') :
				   this.formData.earlySegue;
		},

		editAssetFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		Modes() {
			return Modes;
		}
	},

	methods: {
		initFormData() {
			this.formData = {};
			this.formData = {
				fadeIn: this.asset?.POIPlaybackView?.FadeIn ??
						this.asset?.POIMarkup?.POIMarkers?.find(p => p.POIType === POITypes.FadeIn)?.Position ?? null,
				fadeInLength: this.asset?.POIMarkup?.POIMarkers?.find(p => p.POIType === POITypes.FadeIn)?.Duration ??
							  null,
				fadeOut: this.asset?.POIPlaybackView?.FadeOut ??
						 this.asset?.POIMarkup?.POIMarkers?.find(p => p.POIType === POITypes.FadeOut)?.Position ?? null,
				fadeOutLength: this.asset?.POIMarkup?.POIMarkers?.find(p => p.POIType === POITypes.FadeOut)?.Duration ??
							   null,
				segue: this.asset?.POIPlaybackView?.Segue ??
					   this.asset?.POIMarkup?.POIMarkers?.find(p => p.POIType === POITypes.Segue)?.Position ?? null,
				earlySegue: this.asset?.POIMarkup?.POIMarkers?.find(p => p.POIType === POITypes.EarlySegue)?.Position ??
							null
			};

			const {fadeIn, fadeOut, segue, earlySegue} = this.formData;
			if (segue) {
				this.formData.segue = this.applyRestrictions(segue, fadeIn, fadeOut);
			}
			if (earlySegue) {
				this.formData.earlySegue = this.applyRestrictions(earlySegue, fadeIn, fadeOut);
			}

			// search POIMarkers for intro values
			if (this.markers) {
				this.getPoiMarkersByType(this.markers.POIMarkers, POITypes.Intro).forEach((marker, index) => {
					const key = index === 0 ? "intro" : `intro${index + 1}`;

					this.formData[key] = this.applyRestrictions(marker.Position, fadeIn, fadeOut);
				});
			}

			// search POIPlaybackView for intro values
			["Intro1", "Intro2", "Intro3"].forEach((introKey, index) => {
				const formKey = index === 0 ? "intro" : `intro${index + 1}`;
				const value = this.asset?.POIPlaybackView?.[introKey];

				if (!this.formData[formKey] && value) {
					this.formData[formKey] = this.applyRestrictions(value, fadeIn, fadeOut);
				}
			});
		},

		getData() {
			let payload = {
				POIMarkup: {
					Length: this.asset?.POIMarkup.Length,
					Path: this.asset?.POIMarkup.Path,
					POIMarkers: []
				},
				POIPlaybackView: {}
			};

			if (this.formData?.fadeIn || this.formData?.fadeInLength) {
				payload.POIMarkup.POIMarkers.push({
					POIType: 101,
					Position: this.formData.fadeIn,
					Duration: this.formData.fadeInLength
				});
				payload.POIPlaybackView.FadeIn = this.formData.fadeIn;
			}

			if (this.formData?.fadeOut || this.formData?.fadeOutLength) {
				payload.POIMarkup.POIMarkers.push({
					POIType: 102,
					Position: this.formData.fadeOut,
					Duration: this.formData.fadeOutLength
				});
				payload.POIPlaybackView.FadeOut = this.formData.fadeOut;
			}

			if (this.formData?.segue) {
				payload.POIMarkup.POIMarkers.push({
					POIType: 103,
					Position: this.formData.segue,
				});
				payload.POIPlaybackView.Segue = this.formData.segue;
			}

			if (this.formData?.earlySegue) {
				payload.POIMarkup.POIMarkers.push({
					POIType: 104,
					Position: this.formData.earlySegue,
				});
			}

			if (this.formData?.intro) {
				payload.POIMarkup.POIMarkers.push({
					POIType: 106,
					Position: this.formData.intro
				});
				payload.POIPlaybackView.Intro1 = this.formData.intro;
			}

			if (this.formData?.intro2) {
				payload.POIMarkup.POIMarkers.push({
					POIType: 106,
					Position: this.formData.intro2
				});
			}

			if (this.formData?.intro3) {
				payload.POIMarkup.POIMarkers.push({
					POIType: 106,
					Position: this.formData.intro3
				});
			}

			if (this.formData.ImportFile) {
				payload.ImportFile = this.formData.ImportFile;
			}

			return payload;
		},

		toggleAssetLocked() {
			let poiMarker = this.formData?.POIMarkup?.POIMarkers.find(p => p.POIType === 104);

			if (poiMarker) {
				this.formData.POIMarkup.POIMarkers.find(p => p.POIType === 104).Static = !this.assetLocked;
			}
			else {
				this.formData.POIMarkup = {
					POIMarkers: [
						{
							POIType: 104,
							Static: !this.assetLocked
						}
					]
				};
			}
		},

		debouncedUpdateFormData: _.debounce(function (field, value) {
			this.updateFormData(field, value);
		}, 1000),

		updateFormData(field, value) {
			if (value?.includes(":")) {
				value = this.formatToSeconds(value, "mm:ss");
			}
			const {fadeIn, fadeOut} = this.formData;
			if ((field === "segue" || field === "earlySegue" || field.includes("intro")) && value) {
				value = this.applyRestrictions(value, fadeIn, fadeOut);
			}
			if (value === null && ["fadeIn", "fadeOut"].includes(field)) {
				this.formData[`${field}Length`] = null;
			}
			this.formData[field] = value;
			emitter.emit("update:marker", {
				field,
				value
			});
		},

		handleUpdateMarkerValue(value) {
			this.markerValue = value;
		},

		handleUpdateRegion({field, value}) {
			this.formData[field] = value;
		},

		setTimeToMarker(field) {
			if (this.formData[field] === null && ["fadeIn", "fadeOut"].includes(field)) {
				this.formData[`${field}Length`] = null;
			}
			this.formData[field] = this.markerValue;
			emitter.emit("update:marker", {
				field,
				value: this.formData[field]
			});
		},

		setMarkerToTime(field) {
			emitter.emit("update:currentMarker", this.formData[field]);
		},

		onDrop(acceptFiles) {
			if (acceptFiles) {
				this.dropzoneUploadLoading = true;
				this.fileDropped = true;
				this.formData.ImportFile = acceptFiles;
				this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
					message: "File imported!"
				});
			}
		},

		addIntro() {
			if (!this.formData.intro2) {
				this.formData.intro2 = (this.formData?.fadeIn + this.formData?.fadeInLength || 0) || 0;
				emitter.emit("addIntro", "intro2");
			}
			else {
				this.formData.intro3 = (this.formData?.fadeIn + this.formData?.fadeInLength || 0) || 0;
				emitter.emit("addIntro", "intro3");
			}
		},

		applyRestrictions(value, min, max) {
			return Math.max(min, Math.min(max, value)) || value;
		}
	},

	watch: {
		asset() {
			this.initFormData();
		}
	},

	mounted() {
		this.initFormData();
	},

	created() {
		const {getRootProps, getInputProps, isDragActive, ...rest} = useDropzone({
			onDrop: this.onDrop,
			acceptedFiles: "audio/*"
		});
		this.dropzoneProps = getRootProps();
		this.dropzoneInputProps = getInputProps();
		this.dropzoneActive = isDragActive;
		this.dropzoneRest = rest;
	}
}
</script>

<style scoped lang="scss">
.fade-in-tab {
	background: linear-gradient(90deg, rgba(255, 0, 0, 0) 0%, rgba(255, 0, 0, 0.20) 100%);
}

.fade-out-tab {
	background: linear-gradient(90deg, rgba(255, 0, 0, 0.20) 0%, rgba(255, 0, 0, 0) 100%);
}

.dropzone {
	width: 100%;
	height: 100%;
	display: flex !important;
	justify-content: center;
	align-items: center;
}
</style>